"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { Building2, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useCompanies, useCurrentCompany } from "@/hooks/api/use-companies";

export function CompanySelector() {
  const { data: session, update } = useSession();
  const [currentCompanyName, setCurrentCompanyName] = useState<string>("Carregando...");
  const [isMounted, setIsMounted] = useState(false);

  // Busca as empresas do usuário
  const companiesQuery = useCompanies({ search: undefined });

  // Busca a empresa atual diretamente como fallback
  const currentCompanyQuery = useCurrentCompany();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Atualiza o nome da empresa atual
  useEffect(() => {
    if (session?.user?.currentCompanyId) {
      // Primeiro tenta encontrar na lista de empresas
      if (companiesQuery.data?.data) {
        const current = companiesQuery.data.data.find(
          (c: any) => c.id === session.user.currentCompanyId
        );
        if (current) {
          setCurrentCompanyName(current.name);
          return;
        }
      }
      
      // Se não encontrou na lista, usa a query direta da empresa atual
      if (currentCompanyQuery.data?.name) {
        setCurrentCompanyName(currentCompanyQuery.data.name);
        return;
      }
      
      // Se ainda está carregando, mantém "Carregando..."
      if (companiesQuery.isLoading || currentCompanyQuery.isLoading) {
        setCurrentCompanyName("Carregando...");
        return;
      }
      
      // Se não encontrou em nenhum lugar, mostra um fallback
      if (!companiesQuery.isLoading && !currentCompanyQuery.isLoading) {
        setCurrentCompanyName("Empresa não encontrada");
      }
    } else {
      setCurrentCompanyName("Nenhuma empresa");
    }
  }, [
    companiesQuery.data?.data,
    companiesQuery.isLoading,
    currentCompanyQuery.data?.name,
    currentCompanyQuery.isLoading,
    session?.user?.currentCompanyId,
  ]);

  const handleCompanyChange = async (companyId: string) => {
    try {
      // Salva no localStorage para persistência
      if (typeof window !== "undefined") {
        localStorage.setItem("currentCompanyId", companyId);
      }

      // Atualiza a sessão com a nova empresa
      await update({
        currentCompanyId: companyId,
      });

      // Atualiza o nome da empresa exibido
      const selected = companiesQuery.data?.data?.find((c: any) => c.id === companyId);
      if (selected) {
        setCurrentCompanyName(selected.name);
      } else {
        // Se não encontrou na lista, recarrega a query da empresa atual
        await currentCompanyQuery.refetch();
      }

      // Aguarda um momento para garantir que a sessão foi atualizada
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Recarrega a página para aplicar os filtros
      window.location.reload();
    } catch (error) {
      console.error("Erro ao trocar empresa:", error);
    }
  };

  if (!isMounted || !session?.user) {
    return null;
  }

  // Se o usuário tem apenas uma empresa, não mostra o seletor
  if (companiesQuery.data?.data && companiesQuery.data.data.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-9 px-3 gap-2"
          style={{ color: "var(--p-text-muted-color)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--p-surface-50)";
            e.currentTarget.style.color = "var(--p-text-color)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "var(--p-text-muted-color)";
          }}
          title="Trocar empresa"
        >
          <Building2 className="h-4 w-4" />
          <span className="hidden sm:inline text-sm font-medium max-w-[200px] truncate">
            {currentCompanyName}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <div className="px-3 py-2 border-b" style={{ borderColor: "var(--p-border-color)" }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--p-text-muted-color)" }}>
            Trocar Empresa
          </p>
        </div>
        <div className="py-1 max-h-96 overflow-y-auto">
          {companiesQuery.isLoading ? (
            <div className="px-3 py-2 text-xs text-center" style={{ color: "var(--p-text-muted-color)" }}>
              Carregando empresas...
            </div>
          ) : companiesQuery.data?.data && companiesQuery.data.data.length > 0 ? (
            <>
              {companiesQuery.data?.data.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => handleCompanyChange(company.id)}
                  className="cursor-pointer flex items-center justify-between"
                >
                  <span className="flex-1" style={{ color: "var(--p-text-color)" }}>{company.name}</span>
                  {session.user.currentCompanyId === company.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </>
          ) : (
            <div className="px-3 py-2 text-xs text-center" style={{ color: "var(--p-text-muted-color)" }}>
              Nenhuma empresa disponível
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
